import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  Container,
} from '@mui/material';
import {
  LayoutDashboard as DashboardIcon,
  Calendar as TimelineIcon,
  Settings as SettingsIcon,
  Smartphone as SmartphoneIcon,
  BookOpen as GuideIcon,
  Info as InfoIcon,
  FolderKanban as ProjectIcon,
  Users as UsersIcon,
  Tablet as TabletIcon,
  Menu as MenuIcon,
  MousePointerClick as ClickIcon,
  BarChart3 as ChartIcon,
  Check as CheckIcon,
} from 'lucide-react';

const HelpView: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 4 }, mb: 4 }}>
      <Typography variant="h4" sx={{ 
        mb: { xs: 3, md: 4 }, 
        color: '#2c2c2c', 
        fontWeight: 'bold',
        fontSize: { xs: '1.5rem', md: '2.125rem' }
      }}>
        Help & About
      </Typography>

      <Grid container spacing={3}>
        {/* Quick Start Guide */}
        <Grid>
          <Card sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <GuideIcon size={28} color="#f59f1f" />
                <Typography variant="h5" sx={{ 
                  color: '#2c2c2c', 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', md: '1.5rem' }
                }}>
                  Quick Start Guide
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid>
                  <Paper sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <DashboardIcon size={24} color="#f59f1f" />
                      <Typography variant="h6" sx={{ 
                        color: '#2c2c2c', 
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}>
                        Dashboard
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                      Get an overview of all your projects at a glance:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        View total projects, active projects, and completion rates
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        See project status distribution and budget overview
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Monitor progress trends across all projects
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Access your 3 most recent projects (tap to expand on mobile)
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid>
                  <Paper sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <ProjectIcon size={24} color="#f59f1f" />
                      <Typography variant="h6" sx={{ 
                        color: '#2c2c2c', 
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}>
                        Projects
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                      Manage your construction projects:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Create new projects with budget, timeline, and tasks
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Filter by status (All, Active, Completed)
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Sort by name, progress, budget, or start date
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        On mobile: Tap project cards to expand/collapse details
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid>
                  <Paper sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <TimelineIcon size={24} color="#f59f1f" />
                      <Typography variant="h6" sx={{ 
                        color: '#2c2c2c', 
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}>
                        Timeline
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                      Visualize your project schedule:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Switch between Month, Quarter, and Year views
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        See project durations and overlaps
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Color-coded bars show project status
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Plan resources and identify conflicts
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid>
                  <Paper sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <UsersIcon size={24} color="#f59f1f" />
                      <Typography variant="h6" sx={{ 
                        color: '#2c2c2c', 
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}>
                        Team
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                      Manage your team members:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Add team members with roles and contact info
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Track availability status
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Filter by availability (All, Available, Unavailable)
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        On mobile: Tap to expand member details
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid>
                  <Paper sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <SettingsIcon size={24} color="#f59f1f" />
                      <Typography variant="h6" sx={{ 
                        color: '#2c2c2c', 
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}>
                        Settings
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                      Customize your experience:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        <strong>User Profile:</strong> Update your personal information and profile picture
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        <strong>Notifications:</strong> Configure email, SMS, and in-app notifications
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        <strong>System Settings:</strong> Set company name, currency, timezone, and theme
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        <strong>User Management (Admin):</strong> Add, edit, or remove users and control access
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Mobile Tips */}
        <Grid>
          <Card sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SmartphoneIcon size={28} color="#f59f1f" />
                <Typography variant="h5" sx={{ 
                  color: '#2c2c2c', 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', md: '1.5rem' }
                }}>
                  Mobile Tips
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, flexShrink: 0 }}>
                      <TabletIcon size={24} color="#f59f1f" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                        Collapsible Cards
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        On mobile, tap project cards, team members, and recent projects to expand/collapse details. Only one item expands at a time to save screen space.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, flexShrink: 0 }}>
                      <MenuIcon size={24} color="#f59f1f" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                        Navigation Menu
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Tap the menu icon (☰) in the top-left to open the navigation drawer. Access all sections: Dashboard, Projects, Timeline, Team, Settings, and Help.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, flexShrink: 0 }}>
                      <ClickIcon size={24} color="#f59f1f" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                        Scrollable Tabs
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        In Settings and other multi-section views, swipe left/right on the horizontal tabs to access all options.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, flexShrink: 0 }}>
                      <ChartIcon size={24} color="#f59f1f" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                        Optimized Charts
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                        Charts and graphs are automatically sized for mobile screens with larger touch targets and readable labels.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* About Section */}
        <Grid>
          <Card sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <InfoIcon size={28} color="#f59f1f" />
                <Typography variant="h5" sx={{ 
                  color: '#2c2c2c', 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', md: '1.5rem' }
                }}>
                  About Investcorp Project Manager
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                    <strong>Version:</strong> 1.0.0
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                    <strong>Built for:</strong> Investcorp Construction Projects
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                    <strong>Platform:</strong> Web-based (Desktop & Mobile Responsive)
                  </Typography>
                </Grid>

                <Grid>
                  <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary', fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                    This project management system helps construction companies efficiently manage projects, track budgets, coordinate teams, and monitor progress in real-time.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.secondary', fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                    Designed with both desktop and mobile users in mind, ensuring seamless access to project information from anywhere.
                  </Typography>
                </Grid>

                <Grid>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, color: '#2c2c2c', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    Key Features
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      'Real-time project tracking',
                      'Budget management & monitoring',
                      'Team member coordination',
                      'Visual timeline & Gantt charts',
                      'Task & subtask management',
                      'Mobile-optimized interface'
                    ].map((feature) => (
                      <Grid>
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                          <CheckIcon size={20} color="#4caf50" />
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
                            {feature}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" sx={{ 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    fontSize: { xs: '0.8rem', md: '0.875rem' }
                  }}>
                    © 2025 Investcorp. All rights reserved.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default HelpView;
